import { TgAccountsI } from "@services/getTgAccounts";
import { IAccount, UserRole } from "@services/http/responses/AccountResponse";
import {
  ActionBaseMe,
  ActionSetMe,
  ActionSetNotifsEmails,
  ActionSetPoolDivider,
  ActionSetPoolSettings,
  ActionSetTgAccounts,
  ActionTypesMeE,
  ActionUpdateMe,
  PoolDivider,
  IStoredSetting,
  ActionAddPoolSetting,
  ActionDeletePoolSetting,
  ActionEditPoolSetting,
  ActionSetMeLoaders,
  ActionSetMeta,
} from "./actionTypes";
import LocalStoreOrder from "@utils/localStoreOrder";
import { USER_META_VERSION_240531, UserMeta } from "@services/editUser";

export interface Me extends IAccount {
  settingsInitialized: boolean;
  settings: IStoredSetting[];
  poolDivider: PoolDivider[];
  tgAccounts: TgAccountsI[];
  loaders?: {
    settings: boolean;
  };
}

const LS_SETTINGS_KEY = "WATTMAP:settings_order";
const order = new LocalStoreOrder(LS_SETTINGS_KEY);

const defaultMe: Me = {
  Id: 0,
  uuid: "",
  about: "",
  avatar: "",
  created: 0,
  email: {
    is_private: false,
    value: "",
  },
  name: "",
  notification_emails: [],
  owner: false,
  telegram: {
    is_private: false,
    value: "",
  },
  telegram_uid: "",
  telegram_bot_link: "",
  telegram_bot_qrcode: "",
  updated: 0,
  settingsInitialized: false,
  settings: [],
  poolDivider: [],
  tgAccounts: [],
  summary_email: false,
  summary_telegram: false,
  twofa: false,
  loaders: {
    settings: true,
  },
  role: UserRole.Unknown,
  m_online: 0,
  m_low: 0,
  m_offline: 0,
};

// const initialState = IS_DEBUG ? mockUser : defaultMe;
const initialState = defaultMe;

const parseAccDataMeta = (acc: IAccount) => {
  if (typeof acc.meta === "string") {
    if (acc.meta === "") {
      delete acc.meta;
      return;
    }
    const data = JSON.parse(acc.meta) as UserMeta;
    if (Array.isArray(data)) {
      delete acc.meta;
      return;
    }
    if (data.Version === undefined) data.Version = USER_META_VERSION_240531;
    for (const id in data.LocationBuildingsOrder) {
      const ord = data.LocationBuildingsOrder[id];
      if (typeof ord === "string")
        data.LocationBuildingsOrder[id] = [ord as unknown as string];
    }
    acc.meta = data;
  }
};

const meReducer = (state: Me = initialState, action: ActionBaseMe): Me => {
  switch (action.type) {
    case ActionTypesMeE.SET_ME:
      const { accData } = (action as ActionSetMe).payload;
      parseAccDataMeta(accData);
      return {
        ...state,
        ...accData,
      };
    case ActionTypesMeE.UPDATE_ME: {
      const { accData } = (action as ActionUpdateMe).payload;
      parseAccDataMeta(accData);
      return {
        ...state,
        ...accData,
      };
    }
    case ActionTypesMeE.SET_META:
      const meta = (action as ActionSetMeta).payload;
      return {
        ...state,
        meta,
      };
    case ActionTypesMeE.CLEAR_ME:
      return {
        ...state,
        ...defaultMe,
      };
    case ActionTypesMeE.SET_POOL_SETTINGS:
      let { poolSettings } = (action as ActionSetPoolSettings).payload;
      if (state.settingsInitialized) {
        order.saveOrder(poolSettings);
      } else {
        poolSettings = order.reorder(poolSettings);
      }
      return {
        ...state,
        settingsInitialized: true,
        settings: poolSettings,
      };
    case ActionTypesMeE.ADD_POOL_SETTING: {
      const addedPool = (action as ActionAddPoolSetting).payload;
      const settings = [...state.settings, addedPool];
      order.saveOrder(settings);
      return { ...state, settings };
    }
    case ActionTypesMeE.EDIT_POOL_SETTING: {
      const editedPool = (action as ActionEditPoolSetting).payload;
      const index = state.settings.findIndex(
        (s) => s.id === editedPool.id
      );
      if (index === -1) return { ...state };
      const settings = [
        ...state.settings.slice(0, index),
        editedPool,
        ...state.settings.slice(index + 1),
      ];
      order.saveOrder(settings);

      return {
        ...state,
        settings,
      };
    }
    case ActionTypesMeE.DELETE_POOL_SETTING: {
      const deletedPoolId = (action as ActionDeletePoolSetting).payload;
      const settings = state.settings.filter((s) => s.id !== deletedPoolId);
      order.saveOrder(settings);
      return {
        ...state,
        settings,
      };
    }
    case ActionTypesMeE.SET_POOL_DIVIDER:
      const { poolDivider } = (action as ActionSetPoolDivider).payload;
      return {
        ...state,
        poolDivider,
      };
    case ActionTypesMeE.SET_NOTIFS_EMAILS:
      const { emails } = (action as ActionSetNotifsEmails).payload;
      return {
        ...state,
        notification_emails: emails,
      };
    case ActionTypesMeE.SET_TG_ACCOUNTS:
      const { tgAccounts } = (action as ActionSetTgAccounts).payload;
      return {
        ...state,
        tgAccounts,
      };
    case ActionTypesMeE.SET_LOADERS: {
      const loaders = (action as ActionSetMeLoaders).payload;
      return {
        ...state,
        loaders,
      };
    }
    default:
      return state;
  }
};

export default meReducer;
